const DEFAULT_PAYMENT_PROVENANCE_NOTE =
    'Bill.com cleared the payment, and Eadin recorded it automatically after verifying it by reading it back';

const TEMPLATE_VARIABLES = {
    SUPPLIER_SHIP_REMINDER: [
        { name: 'poNumber', label: 'PO number', sample: '412' },
        { name: 'shipDate', label: 'Ship date', sample: 'September 2, 2026' },
        { name: 'daysToShipment', label: 'Days to shipment', sample: '7' },
        { name: 'label', label: 'Timing label', sample: 'in 7 days' },
        { name: 'vendorName', label: 'Vendor name', sample: 'Hongbang' },
    ],
    SUPPLIER_SHIP_REMINDER_DUE: [
        { name: 'poNumber', label: 'PO number', sample: '412' },
        { name: 'shipDate', label: 'Ship date', sample: 'September 2, 2026' },
        { name: 'daysToShipment', label: 'Days to shipment', sample: '0' },
        { name: 'vendorName', label: 'Vendor name', sample: 'Hongbang' },
    ],
    SUPPLIER_PAYMENT_SENT: [
        { name: 'poNumber', label: 'PO number', sample: 'PO 412' },
        { name: 'vendorName', label: 'Vendor name', sample: 'Hongbang' },
        { name: 'amount', label: 'Amount', sample: '$25,112.50' },
        { name: 'paymentType', label: 'Payment type', sample: 'Balance' },
        { name: 'description', label: 'Description', sample: 'HBMJ2593 PO 412 Balance Pmt' },
        { name: 'datePaid', label: 'Date paid', sample: 'Wednesday, September 2, 2026' },
        { name: 'transactionId', label: 'Transaction ID', sample: 'P26090202 - 6044647' },
        {
            name: 'provenance',
            label: 'Provenance note',
            sample: DEFAULT_PAYMENT_PROVENANCE_NOTE,
        },
    ],
};

const PREVIEWABLE_TEMPLATE_CODES = [
    'SUPPLIER_SHIP_REMINDER',
    'SUPPLIER_SHIP_REMINDER_DUE',
    'SUPPLIER_PAYMENT_SENT',
];

const MAX_SUBJECT_LENGTH = 200;
const MAX_BODY_LENGTH = 20000;
const MAX_PROVENANCE_LENGTH = 500;

const escapeHtml = (value) => String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const extractTemplateTokens = (bodyHtml) => {
    if (typeof bodyHtml !== 'string') return [];
    const tokens = [];
    const seen = new Set();
    const pattern = /{{\s*([A-Za-z][A-Za-z0-9_]*)\s*}}/g;
    let match;
    while ((match = pattern.exec(bodyHtml))) {
        if (!seen.has(match[1])) {
            seen.add(match[1]);
            tokens.push(match[1]);
        }
    }
    return tokens;
};

const hasUnsafeMarkup = (value) => (
    /<script\b|<iframe\b|<object\b|<embed\b|javascript:|\bon[a-z]+\s*=/i.test(value)
);

const validateTemplateBody = (code, bodyHtml) => {
    if (bodyHtml === null || bodyHtml === undefined) return null;
    if (typeof bodyHtml !== 'string') return 'config.body_html must be a string or null';
    if (bodyHtml.length > MAX_BODY_LENGTH) {
        return `config.body_html must be at most ${MAX_BODY_LENGTH} characters`;
    }
    if (hasUnsafeMarkup(bodyHtml)) return 'config.body_html contains unsafe markup';
    const allowed = new Set((TEMPLATE_VARIABLES[code] || []).map(({ name }) => name));
    const unknown = extractTemplateTokens(bodyHtml).find((token) => !allowed.has(token));
    return unknown ? `config.body_html contains unsupported token: ${unknown}` : null;
};

const validateTemplateSubject = (code, subject) => {
    if (subject === null || subject === undefined) return null;
    if (typeof subject !== 'string' || !subject.trim()) {
        return 'config.subject must be a non-empty string or null';
    }
    if (subject.length > MAX_SUBJECT_LENGTH) {
        return `config.subject must be at most ${MAX_SUBJECT_LENGTH} characters`;
    }
    const allowed = new Set((TEMPLATE_VARIABLES[code] || []).map(({ name }) => name));
    const unknown = extractTemplateTokens(subject).find((token) => !allowed.has(token));
    if (unknown) return `config.subject contains unsupported token: ${unknown}`;
    return null;
};

const validateProvenanceNote = (code, value) => {
    if (value === null || value === undefined) return null;
    if (typeof value !== 'string' || !value.trim()) {
        return 'config.provenance_note must be a non-empty string or null';
    }
    if (value.length > MAX_PROVENANCE_LENGTH) {
        return `config.provenance_note must be at most ${MAX_PROVENANCE_LENGTH} characters`;
    }
    if (hasUnsafeMarkup(value)) return 'config.provenance_note contains unsafe markup';
    if (extractTemplateTokens(value).length > 0) {
        return 'config.provenance_note must not contain template variables';
    }
    if (!(TEMPLATE_VARIABLES[code] || []).some(({ name }) => name === 'provenance')) {
        return 'config.provenance_note is not supported for this notification';
    }
    return null;
};

const renderTemplate = ({ body, variables = {}, escapeValues }) => {
    const values = variables || {};
    return String(body == null ? '' : body).replace(
        /{{\s*([A-Za-z][A-Za-z0-9_]*)\s*}}/g,
        (_match, token) => escapeValues ? escapeHtml(values[token]) : String(values[token] == null ? '' : values[token])
    );
};

const renderNotificationTemplate = ({ body, variables = {} }) => (
    renderTemplate({ body, variables, escapeValues: true })
);

const renderTemplateText = ({ body, variables = {} }) => (
    renderTemplate({ body, variables, escapeValues: false })
);

module.exports = {
    TEMPLATE_VARIABLES,
    PREVIEWABLE_TEMPLATE_CODES,
    DEFAULT_PAYMENT_PROVENANCE_NOTE,
    MAX_SUBJECT_LENGTH,
    MAX_BODY_LENGTH,
    MAX_PROVENANCE_LENGTH,
    escapeHtml,
    extractTemplateTokens,
    validateTemplateBody,
    validateTemplateSubject,
    validateProvenanceNote,
    renderNotificationTemplate,
    renderTemplateText,
};
